import type { AutomationRuleDto, WeatherAlertCategory, WeatherAlertSeverity, WeatherTrigger } from '@/lib/types';

export const ALERT_CATEGORY_LABEL: Record<WeatherAlertCategory, string> = {
  tornado: 'Tornado',
  severe_thunderstorm: 'Severe thunderstorm',
  flood: 'Flood',
  hurricane: 'Hurricane / tropical',
  winter: 'Winter storm',
  extreme_heat: 'Extreme heat',
  extreme_cold: 'Extreme cold',
  wind: 'High wind',
  fire: 'Fire weather',
  air_quality: 'Air quality',
  fog: 'Fog',
  marine: 'Marine',
  other: 'Other',
};

export const SEVERITY_LABEL: Record<WeatherAlertSeverity, string> = { warning: 'Warning', watch: 'Watch', advisory: 'Advisory', statement: 'Statement' };

const METRIC_LABEL: Record<string, string> = { rain_probability: 'Chance of rain', precipitation: 'Rainfall', lightning: 'Thunderstorms', heat_index: 'Heat index', wind_speed: 'Wind', temperature_low: 'Low temp', temperature_high: 'High temp' };
const METRIC_UNIT: Record<string, string> = { rain_probability: '%', precipitation: ' in', heat_index: '°F', wind_speed: ' mph', temperature_low: '°F', temperature_high: '°F' };

/** "Tornado or flood warnings in the next 12h", "Heat index ≥ 103°F in the next 12h", "Every weekday at 6:00 AM". */
export function describeTrigger(rule: Pick<AutomationRuleDto, 'kind' | 'trigger' | 'scheduleLabel'>): string {
  if (rule.kind === 'schedule') return rule.scheduleLabel ?? '';
  const t = rule.trigger as WeatherTrigger;
  if (t.metric === 'alert') {
    const kinds = t.alertCategories?.length ? t.alertCategories.map((c) => ALERT_CATEGORY_LABEL[c].toLowerCase()).join(' or ') : 'any';
    const sev = describeSeverities(t.alertSeverities);
    return `${kinds === 'any' ? `Any official ${sev}` : `${capitalize(kinds)} ${sev}`} in the next ${t.lookaheadHours}h`;
  }
  if (t.metric === 'lightning') return `Thunderstorms in the next ${t.lookaheadHours}h`;
  return `${METRIC_LABEL[t.metric] ?? t.metric} ${t.op === '>=' ? '≥' : '≤'} ${t.value}${METRIC_UNIT[t.metric] ?? ''} in the next ${t.lookaheadHours}h`;
}

export function describeSeverities(severities: WeatherAlertSeverity[] | undefined): string {
  const list: WeatherAlertSeverity[] = severities?.length ? severities : ['warning', 'watch'];
  if (list.length === 4) return 'alerts of any severity';
  if (list.length === 1 && list[0] === 'warning') return 'warnings';
  if (list.length === 2 && list.includes('warning') && list.includes('watch')) return 'warnings or watches';
  return list.map((s) => `${SEVERITY_LABEL[s].toLowerCase()}s`).join(' or ');
}

/** How the forecast source is named to people. */
export function weatherProviderLabel(name: string | null | undefined): string {
  if (name === 'openweathermap') return 'OpenWeatherMap';
  if (name === 'open-meteo') return 'Open-Meteo';
  if (name === 'mock') return 'Simulated forecast (development)';
  return name ?? 'Unknown source';
}

/** Tone classes for an alert by severity: warnings are red, watches amber, the rest quiet. */
export function alertTone(severity: WeatherAlertSeverity): { border: string; bg: string; text: string; badge: 'danger' | 'warning' | 'outline' } {
  if (severity === 'warning') return { border: 'border-destructive/50', bg: 'bg-destructive/5', text: 'text-destructive', badge: 'danger' };
  if (severity === 'watch') return { border: 'border-warning/60', bg: 'bg-warning/10', text: 'text-foreground', badge: 'warning' };
  return { border: 'border-border', bg: 'bg-muted/40', text: 'text-foreground', badge: 'outline' };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** How the translation source is named to people. */
export function translationProviderLabel(name: string | null | undefined): string {
  if (name === 'azure') return 'Azure AI Translator';
  if (name === 'mock') return 'Simulated translation (development)';
  if (name === 'none') return 'Off';
  return name ?? 'Unknown source';
}

/** How the address lookup source is named to people. */
export function geocodingProviderLabel(name: string | null | undefined): string {
  if (name === 'geocode-maps-co') return 'geocode.maps.co';
  if (name === 'nominatim') return 'OpenStreetMap Nominatim';
  if (name === 'azure-maps') return 'Azure Maps';
  if (name === 'mock') return 'Simulated lookup (development)';
  return name ?? 'Unknown source';
}
